import type { Audit, AuditType } from "@/services/api/guardiansv2/types";

const UNGROUPED_KEY = "__ungrouped__";

/** Typical pipeline order when timestamps tie. */
const AUDIT_TYPE_EXECUTION_ORDER: Record<AuditType, number> = {
  trigger_run: 0,
  mission_complete: 1,
  interaction: 2,
  task_complete: 3,
  tool_call: 4,
  artifact: 5,
};

/** Milliseconds since epoch for audit list ordering and timestamps. */
export function auditActivityTimestamp(audit: Audit): number {
  return Date.parse(audit.updated_at || audit.created_at) || 0;
}

/** Sort audits newest-first; break ties with execution order (reversed). */
export function compareAuditsByRecency(a: Audit, b: Audit): number {
  const ta = auditActivityTimestamp(a);
  const tb = auditActivityTimestamp(b);
  if (ta !== tb) return tb - ta;
  return (
    AUDIT_TYPE_EXECUTION_ORDER[b.audit_type] -
    AUDIT_TYPE_EXECUTION_ORDER[a.audit_type]
  );
}

/** Sort audits oldest-first (execution order), then by audit type. */
export function compareAuditsByExecution(a: Audit, b: Audit): number {
  const ta = auditActivityTimestamp(a);
  const tb = auditActivityTimestamp(b);
  if (ta !== tb) return ta - tb;
  return (
    AUDIT_TYPE_EXECUTION_ORDER[a.audit_type] -
    AUDIT_TYPE_EXECUTION_ORDER[b.audit_type]
  );
}

export interface AuditMissionGroup {
  key: string;
  missionId: string | null;
  audits: Audit[];
}

/** Group audits by ``scope.mission_id`` for list section headers. */
export function groupAuditsByMission(audits: Audit[]): AuditMissionGroup[] {
  const map = new Map<string, Audit[]>();

  for (const audit of audits) {
    const key = audit.scope.mission_id ?? UNGROUPED_KEY;
    const list = map.get(key) ?? [];
    list.push(audit);
    map.set(key, list);
  }

  const groups: AuditMissionGroup[] = [];
  for (const [key, items] of map) {
    const sorted = [...items].sort(compareAuditsByRecency);
    groups.push({
      key,
      missionId: key === UNGROUPED_KEY ? null : key,
      audits: sorted,
    });
  }

  return groups;
}

export type AuditListEntry =
  | { kind: "single"; audit: Audit; latestAt: number }
  | { kind: "mission"; missionId: string; audits: Audit[]; latestAt: number };

/**
 * Build a recency-sorted feed mixing standalone audits and mission groups.
 * Mission groups sort by their newest audit; entries inside groups are newest-first.
 */
export function buildAuditListEntries(audits: Audit[]): AuditListEntry[] {
  const byMission = new Map<string, Audit[]>();
  const singles: Audit[] = [];

  for (const audit of audits) {
    const missionId = audit.scope.mission_id;
    if (missionId) {
      const list = byMission.get(missionId) ?? [];
      list.push(audit);
      byMission.set(missionId, list);
    } else {
      singles.push(audit);
    }
  }

  const entries: AuditListEntry[] = [];

  for (const [missionId, items] of byMission) {
    const sorted = [...items].sort(compareAuditsByRecency);
    entries.push({
      kind: "mission",
      missionId,
      audits: sorted,
      latestAt: auditActivityTimestamp(sorted[0]),
    });
  }

  for (const audit of singles) {
    entries.push({
      kind: "single",
      audit,
      latestAt: auditActivityTimestamp(audit),
    });
  }

  entries.sort((a, b) => b.latestAt - a.latestAt);
  return entries;
}
